import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import {
  ButtonComponent,
  ButtonGroupComponent,
  ChatBubbleComponent,
  DsChipComponent,
  TranslatePipe,
} from '@softline/ui-core';
import { AiAgentChatService } from '../../services/ai-agent-chat.service';
import { AiAgentContextService } from '../../services/ai-agent-context.service';
import { AgUiContext } from '../../types/ag-ui.types';
import { ChatMention } from '../../types/chat-mention.type';
import { AiAgentComposerComponent } from '../composer/composer.component';
import { AiAgentMessageComponent } from '../message/message.component';

/**
 * The conversation itself — message list plus composer — without any page
 * chrome, so the chat page and the shell panel render the same thread.
 */
@Component({
  selector: 'soft-ai-agent-conversation',
  templateUrl: './conversation.component.html',
  styleUrls: ['./conversation.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.compact]': 'compact()' },
  imports: [
    TranslatePipe,
    ChatBubbleComponent,
    ButtonComponent,
    ButtonGroupComponent,
    DsChipComponent,
    AiAgentComposerComponent,
    AiAgentMessageComponent,
  ],
})
export class AiAgentConversationComponent {
  protected readonly service = inject(AiAgentChatService);
  protected readonly context = inject(AiAgentContextService);

  /** Panel rendering: tighter spacing and an icon-only send button. */
  readonly compact = input(false);

  protected submit(event: { text: string; mentions: ChatMention[] }): void {
    if (!this.service.canSend()) return;
    this.service.send(event.text, {
      mentions: event.mentions,
      context: this.turnContext(event.mentions),
    });
  }

  protected sendFollowup(prompt: string): void {
    if (!this.service.canSend()) return;
    this.service.send(prompt, { context: this.context.forTurn() });
  }

  /** Mentions ride along as a sidecar entry so the model gets the ids, not the labels. */
  private turnContext(mentions: ChatMention[]): AgUiContext[] {
    const entries = this.context.forTurn();
    if (mentions.length === 0) return entries;
    return [...entries, { description: 'mentions', value: JSON.stringify(mentions) }];
  }
}
